/**
 * Device Sync wire types — mirrors iOS cityVisit-chunk-v1 / cityVisit-v1 body shapes.
 */

export const DEVICE_SYNC_SCHEMA_VERSION = 1;
/** Trip export / Device Sync body version — 1.5 adds optional cruiseLine / shipName / cruiseType on trip-v1. */
export const DEVICE_SYNC_EXPORT_SCHEMA_VERSION = '1.5';

export const DeviceSyncRecordType = {
  catalog: 'sync-catalog-v1',
  trip: 'trip-v1',
  cityChunk: 'cityVisit-chunk-v1',
  cityVisit: 'cityVisit-v1',
} as const;

export type DeviceSyncItemType =
  | 'activity'
  | 'meal'
  | 'accommodation'
  | 'transport'
  | 'note'
  | 'other';

export interface DeviceSyncLocation {
  id: string;
  address: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface DeviceSyncReminder {
  id: string;
  minutesBefore: number;
}

export interface DeviceSyncAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  createdAt: string;
}

export interface DeviceSyncBarcode {
  type: string;
  payload: string;
  imagePath?: string | null;
  imageUrl?: string | null;
  imageHint?: string | null;
}

export interface DeviceSyncItemOption {
  id: string;
  itemDescription?: string | null;
  optionDescription?: string | null;
  url?: string | null;
  address?: string | null;
  isDone?: boolean;
  order?: number;
}

export interface DeviceSyncItem {
  id: string;
  type: string;
  title: string;
  startTime: string | null;
  endTime: string | null;
  timeZone: string | null;
  location: DeviceSyncLocation | null;
  confirmation: string | null;
  reservationRequirement: string | null;
  provider: string | null;
  phone: string | null;
  email: string | null;
  url: string | null;
  reservationUrl: string | null;
  itemDescription: string | null;
  price: number | null;
  currency: string | null;
  priorityScore: number;
  metadata: Record<string, string>;
  reminders: DeviceSyncReminder[];
  attachments: DeviceSyncAttachment[];
  options: DeviceSyncItemOption[];
  barcode: DeviceSyncBarcode | null;
  deletedAt: string | null;
}

export interface DeviceSyncDay {
  id: string;
  date: string;
  city: string | null;
  timeZone: string | null;
  notes: string | null;
  /** Schema 1.3 — last content mutation on this day (ISO-8601); omit when unknown */
  updatedAt?: string | null;
  items: DeviceSyncItem[];
}

export interface DeviceSyncHotelConfirmation {
  id: string;
  confirmationNumber: string;
}

export interface DeviceSyncHotelStay {
  id: string;
  hotelName: string;
  checkInDate: string | null;
  checkOutDate: string | null;
  address: string | null;
  phone: string | null;
  notes: string | null;
  url?: string | null;
  provider?: string | null;
  hotelId?: string | null;
  offerId?: string | null;
  deepLink?: string | null;
  photoUrl?: string | null;
  rating?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  confirmations: DeviceSyncHotelConfirmation[];
}

export interface DeviceSyncCityOption {
  id: string;
  itemDescription: string;
  optionDescription: string | null;
  url: string | null;
  address: string | null;
  isDone: boolean;
  order: number;
}

export interface DeviceSyncLargeCityBlobs {
  toursDataFileId?: string | null;
  aiGeneratedPlanDataFileId?: string | null;
  cityPhotoDataFileId?: string | null;
  toursDataCursor?: string | null;
  cityPhotoSource?: string | null;
  cityPhotoAttribution?: string | null;
}

export interface DeviceSyncCityVisit {
  cityVisitId: string;
  cityName: string;
  latitude?: number | null;
  longitude?: number | null;
  timeZoneIdentifier?: string | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  countryName?: string | null;
  countryISO2?: string | null;
  currencyCode?: string | null;
  isCruisePort?: boolean | null;
  cruisePortStatus?: string | null;
  dockNumber?: string | null;
  portArrivalTime?: string | null;
  portDepartureTime?: string | null;
  hotelStays: DeviceSyncHotelStay[];
  options: DeviceSyncCityOption[];
  largeCityBlobs?: DeviceSyncLargeCityBlobs | null;
  attachmentFileIds: string[];
  days: DeviceSyncDay[];
  /** Present on granular cityVisit-v1 bodies */
  tripId?: string;
  deleted?: boolean;
  schemaVersion?: number;
  exportSchemaVersion?: string;
}

export interface DeviceSyncAttachmentRef {
  attachmentId: string;
  fileId: string;
}

export interface DeviceSyncCityVisitChunkBody {
  schemaVersion: number;
  exportSchemaVersion: string;
  tripId: string;
  chunkIndex: number;
  cityVisits: DeviceSyncCityVisit[];
  attachmentRefs: DeviceSyncAttachmentRef[];
}

export interface DeviceSyncChunkEnvelope {
  id: string;
  type: string;
  body: DeviceSyncCityVisitChunkBody;
}
